import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nTextField,
	I18nTextareaField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { costCategoryService } from "../shared/api/costCategoryService";
import {
	CostCategoryFormValues,
	CostCategoryTranslationEntry,
	CostCategoryUpdateResource,
} from "../shared/types/costCategory";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: CostCategoryFormValues) => ({
		...validateEachLanguage(values.translations, (_, value) => {
			const errors: FormikErrors<CostCategoryTranslationEntry> = {};
			if (!value.name) {
				errors.name = t("Required field");
			}
			return errors;
		}),
	});
};

const emptyTranslation = {
	name: "",
	description: null,
};

const CostCategoryFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required />
			<I18nTextareaField name="description" label={t("Description")} />
		</I18nFormDrawerContent>
	);
};

export const CostCategoryCreateDrawer: FC<CreateDrawerFormProps<CostCategoryFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();
	const initialValues: CostCategoryFormValues = useMemo(
		() => ({
			translations: [{ locale: languageSettings.fallback, ...emptyTranslation }],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create cost category")}
			validate={validate}
			renderForm={(props) => <CostCategoryFormDrawerContent {...props} />}
		/>
	);
};

export const CostCategoryUpdateDrawer: FC<UpdateDrawerFormProps<CostCategoryUpdateResource>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit cost category")}
			queryFn={() => costCategoryService.show(props.id).then((x) => x.data.data)}
			queryKey={costCategoryService.showKey(props.id)}
			validate={validate}
			renderSuccess={(props) => <CostCategoryFormDrawerContent {...props} />}
		/>
	);
};
